/**
 * =============================================================================
 * WATCHLIST WIDGET COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Widget wrapper around TodoStack - Secondary information section.
 * 
 * DESIGN SYSTEM:
 * - Section header: 18-20px
 * - 8pt spacing grid
 * - Minimal container styling
 * - No decorative elements
 */

import { Box, Stack, Text, ScrollArea, Group, ActionIcon } from '@mantine/core';
import { Plus } from '@phosphor-icons/react';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { WatchlistWidgetProps } from '../../types';
import HoverTooltip from '@components/common/HoverTooltip';

// Import existing TodoStack from TodoChat
import { TodoStack } from '@components/TodoChat';
import type { AlertItem } from '@components/TodoChat';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 24,
} as const;

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const WatchlistWidget: React.FC<WatchlistWidgetProps> = memo(({
    config,
    className = '',
}) => {
    const navigate = useNavigate();
    
    // Handle alert click - reuses existing navigation pattern
    const handleAlertClick = useCallback((alert: AlertItem) => {
        const path = config.onItemClickPath.replace(':id', alert.id);
        navigate(path);
    }, [navigate, config.onItemClickPath]);
    
    // Handle create alert click
    const handleCreateAlert = useCallback(() => {
        navigate('/alerts/new');
    }, [navigate]);
    
    return (
        <Stack className={`h-full ${className}`} style={{ gap: 0 }}>
            {/* Header - 18px with add button */}
            <Group justify="space-between" align="flex-start" style={{ paddingBottom: spacing.sm }}>
                <Box>
                    <Text 
                        className="text-slate-900"
                        style={{ 
                            fontSize: 18, 
                            fontWeight: 600,
                            lineHeight: 1.3 
                        }}
                    >
                        {config.title}
                    </Text>
                    {config.subtitle && (
                        <Text 
                            className="text-slate-400"
                            style={{ fontSize: 13, marginTop: 4 }}
                        >
                            {config.subtitle}
                        </Text>
                    )}
                </Box>
                
                {/* Add Action Trigger Button */}
                <HoverTooltip label="Add more action triggers" position="left">
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={handleCreateAlert}
                        style={{
                            color: 'rgb(148, 163, 184)',
                            transition: 'color 150ms ease',
                        }}
                        className="hover:!text-slate-700"
                    >
                        <Plus size={18} weight="bold" />
                    </ActionIcon>
                </HoverTooltip>
            </Group>
            
            {/* Watchlist content - wraps TodoStack with scroll. A 0px flex basis keeps the list
                from adding its full height to the row: the box takes whatever height the row
                already has (set by the column beside it) and scrolls inside that. */}
            <Box className="overflow-hidden" style={{ flex: '1 1 0px', minHeight: 0 }}>
                <ScrollArea className="h-full" type="auto">
                    <TodoStack onAlertClick={handleAlertClick} />
                </ScrollArea>
            </Box>
        </Stack>
    );
});

WatchlistWidget.displayName = 'WatchlistWidget';

export default WatchlistWidget;
